/**
 * TakerNav — shared top navigation for all standalone taker pages.
 *
 * Three-destination nav (TSB Website · Community · My Roadmap) with hover
 * tooltip cards explaining each space. Optional right-side CTA button (e.g.
 * "Join the Call", "Enrol Now").
 *
 * Usage:
 *   <TakerNav ctaHref={zoomUrl} ctaLabel="Join the Call" />
 *   <TakerNav />  ← no CTA (informational pages)
 *
 * Enforcement: every new taker page that renders its own layout MUST use this
 * component (or justify the exception in the PR). See main.tsx for the
 * infrastructure contract comment.
 */

const NAV_DESTINATIONS = [
  {
    label: 'TSB Website',
    href: 'https://www.thesuccessfulbookkeeper.com',
    tooltipTitle: 'TSB Website',
    tooltipBody: 'The Successful Bookkeeper home — articles, podcast episodes, and everything in the TSB world.',
  },
  {
    label: 'Community',
    href: 'https://community.thesuccessfulbookkeeper.com',
    tooltipTitle: 'Community',
    tooltipBody: 'Your program community — discussions, workbooks, recordings, and your peers.',
  },
  {
    label: 'My Roadmap',
    href: 'https://myroadmap.thesuccessfulbookkeeper.com',
    tooltipTitle: 'My Roadmap',
    tooltipBody: 'Your personal progress hub — sessions, milestones, and what\'s next in your program.',
  },
];

const TSB_DEFAULT_LOGO = 'https://pub-21e4931ef4774e378bca73afe99a4ba4.r2.dev/brands/tsb/logo-dark.svg';

interface TakerNavProps {
  /** Optional CTA button on the right (e.g. "Join the Call", "Enrol Now"). */
  ctaHref?: string | null;
  ctaLabel?: string;
  /** Logo override (e.g. a resolved brand/sub-brand lockup). Defaults to the
   *  TSB dark-nav logo — every existing caller is unaffected. */
  logoUrl?: string | null;
  logoAlt?: string;
  /** Where the logo links to. Defaults to the TSB website. */
  logoHref?: string | null;
}

export default function TakerNav({
  ctaHref, ctaLabel = 'Join',
  logoUrl, logoAlt = 'The Successful Bookkeeper', logoHref,
}: TakerNavProps) {
  // An in-page anchor CTA (e.g. "#ee-optin") should smooth-scroll to the
  // target on this page, not open a new tab to a URL fragment on itself.
  const isAnchor = !!ctaHref && ctaHref.startsWith('#');
  return (
    // Layout lives in TakerShell's SHELL_STYLES (.tsb-taker-nav*), not inline:
    // inline styles can't carry the phone-width media query that lets the
    // link row wrap under the logo instead of widening the page.
    <nav className="tsb-taker-nav">
      {/* Logo */}
      <a
        href={logoHref ?? 'https://www.thesuccessfulbookkeeper.com'}
        target="_blank" rel="noopener noreferrer"
        className="tsb-taker-nav-logo"
      >
        <img
          src={logoUrl ?? TSB_DEFAULT_LOGO}
          alt={logoAlt}
          style={{ height: 36, width: 'auto', maxWidth: '100%', objectFit: 'contain', display: 'block' }}
          onError={(e) => {
            // Fallback to text wordmark if image fails
            const img = e.currentTarget;
            img.style.display = 'none';
            const fallback = img.nextElementSibling as HTMLElement | null;
            if (fallback) fallback.style.display = 'inline';
          }}
        />
        <span style={{ color: '#fff', fontFamily: "'Fraunces', serif", fontSize: 17, fontWeight: 700, display: 'none' }}>
          The Successful <span style={{ color: '#F05A28' }}>Bookkeeper</span>
        </span>
      </a>

      {/* Nav links + tooltips */}
      <div className="tsb-taker-nav-links">
        {NAV_DESTINATIONS.map(({ label, href, tooltipTitle, tooltipBody }) => (
          <div key={label} className="tsb-nav-item">
            <a href={href} target="_blank" rel="noopener noreferrer" className="tsb-nav-link">
              {label}
            </a>
            <div className="tsb-nav-tooltip">
              <div style={{ fontSize: 12, fontWeight: 700, color: '#F05A28', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.8 }}>
                {tooltipTitle}
              </div>
              <div style={{ fontSize: 13, color: '#374151', lineHeight: 1.55 }}>
                {tooltipBody}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* CTA */}
      {ctaHref && (
        <a
          href={ctaHref}
          {...(isAnchor ? {} : { target: '_blank', rel: 'noopener noreferrer' })}
          onClick={isAnchor ? (e) => {
            e.preventDefault();
            document.getElementById(ctaHref.slice(1))?.scrollIntoView({ behavior: 'smooth' });
          } : undefined}
          className="cta-btn tsb-taker-nav-cta"
          style={{
            background: '#F05A28', color: '#fff', padding: '8px 20px',
            borderRadius: 6, fontSize: 13, fontWeight: 700, textDecoration: 'none', flexShrink: 0,
          }}
        >
          {ctaLabel}
        </a>
      )}
    </nav>
  );
}
